import { useState, useEffect, useMemo, useCallback } from 'react';
import { DeliveryHistory, DeliveryHistoryFilter, DeliveryHistoryResponse } from '../types/deliveryHistory.types';
import { DeliveryHistoryApiResponse, DeliveryHistoryRawItem } from '../types/deliveryHistory.api.types';
import { deliveryHistoryApi } from '../api/deliveryHistoryApi';
import { useAsync } from '@/shared/hooks/useAsync';
import {
    extractArrayFromResponse,
    formatYmdToDate,
    toSafeNumber,
    toSafeString,
} from '@/shared/utils/apiResponseHelpers';
import { calculateDeliveryCountTotals, EMPTY_DELIVERY_COUNT_TOTALS } from '@/shared/utils/aggregationUtils';

const DEFAULT_PAGE_SIZE = 10;

const mapDeliveryHistoryData = (raw: DeliveryHistoryApiResponse): DeliveryHistory[] => {
    const list = extractArrayFromResponse<DeliveryHistoryRawItem>(raw as DeliveryHistoryRawItem[], 'delivery');
    return list.map(
        (item): DeliveryHistory => ({
            supplyId: toSafeString(item.supplyId ?? item.suppId),
            suppName: item.suppName ? toSafeString(item.suppName) : undefined,
            riderId: toSafeString(item.riderId),
            riderName: item.riderName ? toSafeString(item.riderName) : undefined,
            phoneNum: item.phoneNum ? toSafeString(item.phoneNum) : undefined,
            deliveryDate: formatYmdToDate(toSafeString(item.deliveryDate)),
            deliveryStatusName: item.deliveryStatusName ? toSafeString(item.deliveryStatusName) : undefined,
            finishCnt: toSafeNumber(item.finishCnt),
            rejectCnt: toSafeNumber(item.rejectCnt),
            assingCancelCnt: toSafeNumber(item.assingCancelCnt),
            deliCancelCnt: toSafeNumber(item.deliCancelCnt),
            morTimeCnt: toSafeNumber(item.morTimeCnt),
            afterNonCnt: toSafeNumber(item.afterNonCnt),
            nightCnt: toSafeNumber(item.nightCnt),
            nightNonCnt: toSafeNumber(item.nightNonCnt),
        })
    );
};

const extractDateMeta = (data: DeliveryHistoryApiResponse): { fromDate?: string; toDate?: string } => {
    if (Array.isArray(data)) return {};
    if (typeof data !== 'object' || data === null) return {};
    const { fromDate, toDate } = data as { fromDate?: string; toDate?: string };
    return { fromDate, toDate };
};

export const useDeliveryData = (
    filter: DeliveryHistoryFilter,
    onDateMetaChange?: (fromDate?: string, toDate?: string) => void
) => {
    const [page, setPage] = useState(1);
    const limit = DEFAULT_PAGE_SIZE;
    const [allData, setAllData] = useState<DeliveryHistory[]>([]);

    const fetchFunction = useMemo(() => () => deliveryHistoryApi.getDeliveryHistory(filter), [filter]);
    const { result, execute: fetchDeliveryHistory, isLoading } = useAsync<DeliveryHistoryApiResponse>(fetchFunction);

    const loading = isLoading;
    const error = result.status === 'error' ? result.error : undefined;

    useEffect(() => {
        if (result.status !== 'success' || !result.data) return;
        setAllData(mapDeliveryHistoryData(result.data));
        const { fromDate, toDate } = extractDateMeta(result.data);
        if ((fromDate || toDate) && onDateMetaChange) {
            onDateMetaChange(fromDate, toDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    const deliveryHistoryData = useMemo((): DeliveryHistoryResponse => {
        if (!allData.length) return { data: [], total: 0, page: 1, limit };
        const startIndex = (page - 1) * limit;
        return {
            data: allData.slice(startIndex, startIndex + limit),
            total: allData.length,
            page,
            limit,
        };
    }, [allData, page, limit]);

    const totals = useMemo(
        () => (allData.length ? calculateDeliveryCountTotals(allData) : EMPTY_DELIVERY_COUNT_TOTALS),
        [allData]
    );

    const pageTotals = useMemo(
        () =>
            deliveryHistoryData.data.length
                ? calculateDeliveryCountTotals(deliveryHistoryData.data)
                : EMPTY_DELIVERY_COUNT_TOTALS,
        [deliveryHistoryData.data]
    );

    const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
    const resetPage = useCallback(() => setPage(1), []);

    return {
        deliveryHistoryData,
        loading,
        error,
        page,
        limit,
        totals,
        pageTotals,
        handlePageChange,
        resetPage,
        fetchDeliveryHistory,
    };
};
